import { ReplayPassType, type ReplayDecisionOption } from "@hooks/common/session/replay-decision-utils";

export interface DecisionScoreRow {
  label: string;
  value: string;
}

type I18nFn = (key: string, params?: Record<string, unknown>) => string;
type DecisionTitleVariant = "short" | "long";

const titleKeyByVariant: Record<DecisionTitleVariant, { user: string; alternative: string }> = {
  short: {
    user: "session-results.feedback.your-decision-short",
    alternative: "session-results.feedback.alternative-decision-short",
  },
  long: {
    user: "session-results.feedback.your-decision",
    alternative: "session-results.feedback.alternative-decision",
  },
};

const formatSignedValue = (value: number, digits = 2) => {
  const normalizedValue = Number.isFinite(value) ? value : 0;
  const scaledValue = normalizedValue * 100;
  const prefix = scaledValue >= 0 ? "+" : "";

  return `${prefix}${scaledValue.toFixed(digits)}%`;
};

const formatPercentValue = (value: number, digits = 0) => {
  const normalizedValue = Number.isFinite(value) ? value : 0;
  return `${(normalizedValue * 100).toFixed(digits)}%`;
};

export const buildDecisionScoreRows = (option: ReplayDecisionOption | null, i18n: I18nFn): DecisionScoreRow[] => {
  if (!option) {
    return [];
  }

  if (option.passType === ReplayPassType.SHOT) {
    return [
      {
        label: i18n("session-results.decision-panel.expected-goals"),
        value: formatPercentValue(option.xG ?? 0),
      },
    ];
  }

  return [
    {
      label: i18n("session-results.decision-panel.expected-pass"),
      value: formatPercentValue(option.xP ?? 0),
    },
    {
      label: i18n("session-results.decision-panel.expected-threat"),
      value: formatSignedValue(option.xT ?? 0),
    },
    {
      label: i18n("session-results.decision-panel.expected-pass-turnover"),
      value: formatPercentValue(option.xPT ?? 0),
    },
  ];
};

export const getDecisionFeedbackMessage = (score: number, i18n: I18nFn) => {
  if (score >= 90) return i18n("session-results.decision-panel.feedback.excellent");
  if (score >= 75) return i18n("session-results.decision-panel.feedback.great");
  if (score >= 50) return i18n("session-results.decision-panel.feedback.good");
  return i18n("session-results.decision-panel.feedback.improve");
};

export const getDecisionTitle = (index: number, didUserMakeDecision: boolean, i18n: I18nFn, variant: DecisionTitleVariant = "long") => {
  if (index === 0) {
    return didUserMakeDecision ? i18n(titleKeyByVariant[variant].user) : i18n("session-results.feedback.no-decision");
  }

  return `${i18n(titleKeyByVariant[variant].alternative)} ${String.fromCharCode(64 + index)}`;
};

export const formatReplayTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};
